/*----------------------------------
- WEBSITE AUTH HREFS
----------------------------------*/

/**
 * @fix  docs/fixes/2026-07-15-website-auth-conversion-flow.md
 */

import {
    buildConversionAuthHref,
    parseConversionAuthIntent,
    type ConversionAuthIntent,
} from '@uniquedomains/components/conversion/authIntent';
import {
    FREE_PLAN_ID,
    normalizeSellablePricingPlanId,
} from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import type { DesiredState } from '@uniquedomains/components/pricing/types';
import { WEBSITE_CONVERSION_OPEN_EVENT } from '@/client/services/conversion/events';

export const buildWebsiteAuthHref = (intent: Partial<ConversionAuthIntent>): string => {
    return buildConversionAuthHref(intent);
};

export const openWebsiteAuthHref = (href: string): boolean => {
    if (typeof window === 'undefined' || !window.CustomEvent) return false;
    window.dispatchEvent(new CustomEvent(WEBSITE_CONVERSION_OPEN_EVENT, { detail: { href } }));
    return true;
};

export const pricingDesiredToWebsiteAuthHref = (
    desired: Pick<DesiredState, 'from' | 'period' | 'page'> & { plan: string },
): string => {
    const isExplorer = desired.plan === FREE_PLAN_ID;
    /* Only the unpaid state and a sellable plan travel as a selection. Since D27 a
       retired id normalizes to Full Access (`member`) and arms its pay-first fast
       path; an id naming no plan drops out and the visitor lands on the default. */
    const selectedPlan = isExplorer
        ? FREE_PLAN_ID
        : normalizeSellablePricingPlanId(desired.plan) ?? undefined;

    return buildWebsiteAuthHref({
        flow: isExplorer ? 'cold' : 'hot',
        mode: 'signup',
        from: desired.from || 'pricing_page',
        /* The pricing page carries the page the visitor came from as `desired.page`, and it
           is a real destination: whoever landed on pricing from a product URL goes back to
           it after signing in. No context needed, the plan already names the click. */
        returnTo: desired.page,
        ...(selectedPlan ? { plan: selectedPlan } : {}),
        ...(selectedPlan && !isExplorer && desired.period ? { period: desired.period } : {}),
    });
};

export const upgradeHrefToWebsiteAuthHref = (href: string): string => {
    const url = new URL(href, 'https://app.unique.domains');
    const planMatch = url.pathname.match(/^\/pricing\/([^/]+)$/);
    const parsed = parseConversionAuthIntent(url.search, planMatch ? [decodeURIComponent(planMatch[1])] : []);
    return buildWebsiteAuthHref({
        ...parsed.intent,
        flow: 'hot',
        mode: 'signup',
        plan: planMatch ? decodeURIComponent(planMatch[1]) : parsed.intent.plan,
    });
};
